import { DefaultError, UseMutationResult } from '@tanstack/react-query';
import { AxiosResponse, isAxiosError } from 'axios';

import { Banner } from '@ingrid/luterkort-react';

import { useTranslation } from '~src/i18n/use-translation';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type MutationType = UseMutationResult<AxiosResponse<unknown>, DefaultError, any>;

export const useMutationError = ({ isError, error }: MutationType) => {
  const { t } = useTranslation();

  if (!isError) {
    return { isError };
  }

  const axiosError = isAxiosError(error);
  if (axiosError) {
    const errorCode = error.response?.status;
    if (errorCode === 401 || errorCode === 403) {
      return { isError, title: t('auth.forbidden') };
    }

    if (errorCode !== undefined && errorCode >= 400 && errorCode < 500) {
      const message =
        (error.response?.data?.details?.msg as string | undefined) ??
        (error.response?.data?.error as string | undefined);

      if (message) {
        return { isError, title: t('errors.generic'), subtitle: message };
      }
    }
  }

  return { isError, title: t('errors.title') };
};

interface Props {
  mutation: MutationType;
}

export function MutationBanner({ mutation }: Props) {
  const { title, subtitle, isError } = useMutationError(mutation);

  if (!isError) {
    return null;
  }

  return <Banner title={title} subtitle={subtitle} variant="critical" onClose={mutation.reset} />;
}
